import { Download, X, Share } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { usePwaInstall } from '@/shared/hooks/usePwaInstall';
import { monitoringService } from '@/shared/services';
import { ButtonSize, ButtonVariant } from '@/shared/types/button.types';

/**
 * PWA install prompt component.
 *
 * Shows a banner prompting the user to install the app:
 * - Chrome/Edge: Triggers native `beforeinstallprompt` flow
 * - iOS Safari: Shows manual "Add to Home Screen" instructions
 *
 * Dismissable for 30 days via localStorage.
 * Hidden when the app is already installed as a PWA.
 */
export function PwaInstallPrompt() {
  const { canInstall, isInstalled, isIos, promptInstall, dismissInstall } =
    usePwaInstall();

  // Don't show if already installed
  if (isInstalled) return null;

  // Show iOS-specific manual instructions
  if (isIos && !canInstall) {
    return <IosInstallHint onDismiss={dismissInstall} />;
  }

  // Show native install prompt (Chrome/Edge)
  if (!canInstall) return null;

  return (
    <div
      className="fixed bottom-4 left-4 z-50 max-w-sm rounded-lg border border-border bg-card p-4 shadow-lg"
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <Download className="size-5 text-primary" aria-hidden="true" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-card-foreground">Install App</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Add Madi &amp; Vladi to your home screen for quick access and
            offline support.
          </p>
          <div className="mt-3 flex gap-2">
            <Button
              size={ButtonSize.Small}
              variant={ButtonVariant.Default}
              onClick={() => {
                monitoringService.trackPwaEvent('pwa.install_prompted');
                promptInstall()
                  .then((accepted) => {
                    if (accepted) {
                      monitoringService.trackPwaEvent('pwa.install_accepted');
                    } else {
                      monitoringService.trackPwaEvent('pwa.install_dismissed');
                    }
                  })
                  .catch((error) => {
                    console.error('[PWA] Install prompt failed:', error);
                  });
              }}
              aria-label="Install application"
            >
              Install
            </Button>
            <Button
              size={ButtonSize.Small}
              variant={ButtonVariant.Ghost}
              onClick={dismissInstall}
              aria-label="Dismiss install prompt"
            >
              Not now
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismissInstall}
          className="text-muted-foreground transition-colors hover:text-foreground"
          aria-label="Close install prompt"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

interface IosInstallHintProps {
  readonly onDismiss: () => void;
}

function IosInstallHint({ onDismiss }: IosInstallHintProps) {
  return (
    <div
      className="fixed bottom-4 left-4 z-50 max-w-sm rounded-lg border border-border bg-card p-4 shadow-lg"
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <Share className="size-5 text-primary" aria-hidden="true" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-card-foreground">Install App</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Tap the <strong>Share</strong> button in Safari, then select{' '}
            <strong>&quot;Add to Home Screen&quot;</strong> for quick access.
          </p>
          <Button
            size={ButtonSize.Small}
            variant={ButtonVariant.Ghost}
            onClick={onDismiss}
            className="mt-2"
            aria-label="Dismiss install instructions"
          >
            Got it
          </Button>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="text-muted-foreground transition-colors hover:text-foreground"
          aria-label="Close install instructions"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
